// ============================================================================
// The Files app's online calls (docs/areas/files.md): presigned URLs from
// /api/files/* with the owner's token, and the bucket itself through them.
// Kept out of db/api.ts: nothing here is sync, and the bytes never touch the
// app's server. Presigned GETs are cached until a minute before they expire.
// ============================================================================

import { AuthError, NetworkError } from '@/db/api'
import { META } from '@/db/schema'
import { db } from '@/runtime'

export type Variant = 'view' | 'thumb' | 'download'

export interface FilesStatus {
  enabled: boolean
  bucket?: string
  origin?: string
  single_put_max?: number
}

export type UploadStart = { url: string } | { upload_id: string; part_size: number; parts: number }

export interface UploadedPart {
  n: number
  etag: string
  size?: number
}

const req = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
  const token = await db.getMeta<string>(META.token)
  if (!token) throw new AuthError()
  let res: Response
  try {
    res = await fetch(path, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  } catch (e) {
    throw new NetworkError((e as Error).message)
  }
  if (res.status === 401) throw new AuthError()
  if (!res.ok) {
    let reason = ''
    try {
      reason = String(((await res.json()) as { error?: string }).error ?? '')
    } catch {
      /* not JSON */
    }
    throw new NetworkError(reason || `${method} ${path}: ${res.status}`)
  }
  return (await res.json()) as T
}

const STATUS_CACHE = 'files-status'

/** Whether Files has a bucket; the last answer is kept on the device for offline. */
export const filesStatus = async (): Promise<FilesStatus> => {
  const st = await req<FilesStatus>('GET', '/api/files/status')
  try {
    localStorage.setItem(STATUS_CACHE, JSON.stringify(st))
  } catch {
    /* private mode */
  }
  return st
}

export const lastFilesStatus = (): FilesStatus | null => {
  try {
    const raw = localStorage.getItem(STATUS_CACHE)
    return raw ? (JSON.parse(raw) as FilesStatus) : null
  } catch {
    return null
  }
}

export const filesApi = {
  uploadStart: (key: string, size: number, content_type: string) =>
    req<UploadStart>('POST', '/api/files/uploads', { key, size, content_type }),
  uploadParts: (key: string, upload_id: string, parts: number[]) =>
    req<{ urls: Record<string, string> }>('POST', '/api/files/uploads/parts', { key, upload_id, parts }),
  uploadedParts: (key: string, upload_id: string) =>
    req<{ parts: UploadedPart[] }>(
      'GET',
      `/api/files/uploads/parts?key=${encodeURIComponent(key)}&upload_id=${encodeURIComponent(upload_id)}`,
    ),
  uploadComplete: (key: string, upload_id: string, parts: UploadedPart[]) =>
    req<{ size: number }>('POST', '/api/files/uploads/complete', { key, upload_id, parts }),
  uploadAbort: (key: string, upload_id?: string) =>
    req<{ ok: boolean }>('POST', '/api/files/uploads/abort', { key, upload_id }),
  password: (password: string) => req<{ hash: string }>('POST', '/api/files/password', { password }),
}

// ---- presigned GETs, cached ---------------------------------------------------

const cache = new Map<string, { url: string; until: number }>()
const MARGIN = 60_000

/** Presigned URLs for files (at most 200 a call), from the cache where still good. */
export const fileUrls = async (ids: string[], variant: Variant): Promise<Record<string, string>> => {
  const now = Date.now()
  const out: Record<string, string> = {}
  const want: string[] = []
  for (const id of ids) {
    const hit = variant !== 'download' ? cache.get(`${variant}:${id}`) : undefined
    if (hit && hit.until - MARGIN > now) out[id] = hit.url
    else want.push(id)
  }
  for (let i = 0; i < want.length; i += 200) {
    const res = await req<{ urls: Record<string, string>; expires_at: string }>('POST', '/api/files/urls', {
      ids: want.slice(i, i + 200),
      variant,
    })
    const until = Date.parse(res.expires_at)
    for (const [id, url] of Object.entries(res.urls)) {
      out[id] = url
      if (variant !== 'download') cache.set(`${variant}:${id}`, { url, until })
    }
  }
  return out
}

export const fileUrl = async (id: string, variant: Variant): Promise<string | undefined> =>
  (await fileUrls([id], variant))[id]
